import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { ArrowRight } from 'lucide-react';

const OnlineArtCoursesPage = () => {
    return (
        <div data-testid="online-art-courses-page">
            <SEO
                title="Online Art Courses for Drawing & Painting"
                description="Study drawing, oil painting, portraiture, and classical techniques in structured online art courses from master artists. Self-paced video lessons from Tony Pro, Jeremy Lipking, Glen Orbik, and more."
                url="/online-art-courses"
                keywords={['online art courses', 'drawing courses online', 'painting courses online', 'art classes online', 'classical art training online']}
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'WebPage',
                    '@id': 'https://zarolla.com/online-art-courses#webpage',
                    url: 'https://zarolla.com/online-art-courses',
                    name: 'Online Art Courses for Drawing & Painting | Zarolla Online Art Academy',
                    description: 'Study drawing, oil painting, portraiture, and classical techniques in structured online art courses from master artists.',
                    isPartOf: { '@id': 'https://zarolla.com/#website' },
                    datePublished: '2007-01-01',
                    dateModified: new Date().toISOString().split('T')[0],
                    inLanguage: 'en-US',
                }}
            />

            <div className="section-gap">
                <div className="container-main">
                    {/* Hero */}
                    <div className="max-w-3xl mb-16">
                        <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                            Learn at your own pace
                        </span>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-primary leading-tight mt-4">
                            Online art courses from master artists.
                        </h1>
                        <p className="text-muted-foreground text-lg mt-6 max-w-2xl">
                            Zarolla offers structured, multi-session art courses taught by professional painters and sculptors. Each course is designed to build real skill — portrait drawing, oil painting, figure work, and classical technique — through clear instruction you can study on your own schedule.
                        </p>
                    </div>

                    {/* Section 1 */}
                    <section className="py-12 border-t border-border">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                            <div>
                                <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                    Courses built for serious artists
                                </h2>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    Every course on Zarolla is a complete learning path — not a random collection of tips. Our instructors include Tony Pro, Jeremy Lipking, Glen Orbik, Adam Reeder, and other accomplished painters and sculptors who have exhibited, won awards, and taught extensively in the classical tradition.
                                </p>
                                <p className="text-muted-foreground leading-relaxed">
                                    Courses range from foundational drawing and head construction to advanced alla prima painting and figure composition. Whether you are a beginner building your first skill set or an experienced painter looking to sharpen specific techniques, there is instruction here that will push your work forward.
                                </p>
                            </div>
                            <div className="bg-secondary/30 rounded-sm p-8">
                                <h3 className="font-medium text-primary mb-4">What you'll find in our courses:</h3>
                                <ul className="space-y-3 text-muted-foreground">
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Multi-session video instruction from start to finish</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Portrait drawing and head construction</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Oil painting technique and color mixing</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Figure drawing and anatomy</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1">—</span>
                                        <span>Classical and contemporary approaches</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Section 2 */}
                    <section className="py-12 border-t border-border">
                        <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                            Why study art online?
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div>
                                <h3 className="font-medium text-primary mb-3">Learn from the best, anywhere</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Access instruction from master artists you might never meet in person. Our instructors have taught at major ateliers and exhibited in galleries nationwide. Their knowledge is now available to you regardless of where you live.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-medium text-primary mb-3">Study on your schedule</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Pause, rewind, and re-watch as many times as you need. Online art courses let you absorb complex demonstrations at your own pace — something even the best in-person class cannot offer.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-medium text-primary mb-3">Build real skill over time</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Multi-session courses let you progress methodically through drawing, value, color, and composition. Each lesson builds on the last, giving you a structured path to genuine improvement.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Section 3: CTA */}
                    <section className="py-12 border-t border-border">
                        <div className="max-w-2xl">
                            <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                Start learning today
                            </h2>
                            <p className="text-muted-foreground leading-relaxed mb-8">
                                Browse our full course catalog to find instruction that matches your interests and level. Each course includes detailed descriptions so you know exactly what you'll learn before you purchase.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
                                    <Link to="/courses">
                                        Browse All Courses <ArrowRight className="ml-2 h-4 w-4" />
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="lg">
                                    <Link to="/workshops">View Painting Videos</Link>
                                </Button>
                                <Button asChild variant="outline" size="lg">
                                    <Link to="/sculptures">View Sculpture Videos</Link>
                                </Button>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default OnlineArtCoursesPage;
